/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CircularProgress, Grid, GridSize, IconButton, Tooltip } from "@mui/material";
import { CSSProperties } from "react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { FiMapPin, FiUser } from "react-icons/fi";
import { HiOutlinePhone } from "react-icons/hi";
import { MdOutlineEmail } from "react-icons/md";
import { VscGlobe } from "react-icons/vsc";
import * as yup from "yup";

import { MOBILE_MAX_SIZE } from "../../../constants";
import { ExternalList } from "../../../context/MovingResources";
import { useAppState } from "../../../context/StateContext";
import useYupValidation from "../../../hooks/useYupValidation";
import { Access } from "../../../models/access";
import { Customer } from "../../../models/customer";
import { Languages } from "../../../models/languages";
import { DifferentLocation, Location } from "../../../models/location";
import { CustomerLocations, RoomJob } from "../../../models/sales";
import { Timezone } from "../../../models/timezone";
import TimezoneService from "../../../services/timezone.service";
import { onConfirmRemoveAddress, onRemoveAddress } from "../../../util/actions";
import { formatPhoneNumber } from "../../../util/formatPhoneNumber";
import Box from "../../Form/Box";
import FieldView from "../../Form/FieldView";
import Input from "../../Form/Input";
import AccessChecklistSummary from "../AccessChecklistSummary";
import CustomerInfoForm from "../CustomerInfo";
import RemoveItemsFromLocationModal, {
  RemoveItemsFromLocationHandles,
} from "../RemoveItemsFromLocationModal";
import AddressInfoForm from "./AddressInfoForm";
import useStyles, {
  ContactInfoWrapper,
  ContactMapInfoWrapper,
  InfoWrapper,
  Required,
  SectionWrapper,
  ViewModeDivider,
  ViewModeTitle,
} from "./styles";

type GridOptions = {
  container?: boolean;
  justifyContent?: CSSProperties["justifyContent"];
  md?: boolean | GridSize | undefined;
  lg?: boolean | GridSize | undefined;
};

type RenderCustomerAttributeProps = {
  icon: React.ReactElement;
  label: string;
  dataCy?: string;
  options?: GridOptions;
};

export type LocationExtensionField =
  | "baseCode"
  | "baseDescription"
  | "areaCode"
  | "areaDescription"
  | "agentName"
  | "agentZip"
  | "agentCity";

export type LocationExtensionValues = Partial<Record<LocationExtensionField, string>>;

function hasNonEmptyLocationExtension(ext?: LocationExtensionValues): boolean {
  if (!ext) return false;
  return Object.values(ext).some((v) => v != null && String(v).trim() !== "");
}

interface Props {
  customerLocations: CustomerLocations;
  access?: Access;
  inventory: RoomJob[];
  title: string;
  isOnViewMode?: boolean;
  isRenderOnViewComponent?: boolean;
  update?: (value: CustomerLocations) => void;
  updateAll?: (inventory: RoomJob[], customerLocation: CustomerLocations) => void;
  onCopyInfo?: (from?: string) => void;
  externalList?: ExternalList;
  isLoading?: boolean;
  onCustomerView?: boolean;
  externalErrors?: yup.ValidationError[];
  contactsErrors?: yup.ValidationError[][];
  locationExtension?: LocationExtensionValues;
  onLocationExtensionChange?: (field: LocationExtensionField, value: string) => void;
  /** When true with isOnViewMode, renders military location extension as FieldView (read-only). */
  showMilitaryLocationExtensionInView?: boolean;
}

const AddressComponent = ({
  title,
  customerLocations,
  access,
  inventory,
  isOnViewMode = false,
  isRenderOnViewComponent = false,
  update,
  updateAll,
  onCopyInfo,
  externalList,
  isLoading = false,
  onCustomerView = false,
  externalErrors = [],
  contactsErrors = [],
  locationExtension,
  onLocationExtensionChange,
  showMilitaryLocationExtensionInView = false,
}: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles(isOnViewMode)();
  const removeItemsRef = useRef<RemoveItemsFromLocationHandles>(null);
  const { getErrorByField, fieldHasError } = useYupValidation(externalErrors);

  const [openInMaintenanceMode, setOpenInMaintenanceMode] = useState<boolean>(false);
  const [timezones, setTimezones] = useState<Array<Timezone>>(externalList?.timezoneList || []);
  const [orderLanguages, setOrderLanguages] = useState<Languages[]>([]);

  const [openInViewMode, setOpenInViewMode] = useState<boolean>(true);

  const [customerLanguage, setCustomerLanguage] = useState<Languages | undefined>({} as Languages);

  const isMobile = window.innerWidth <= MOBILE_MAX_SIZE;

  const { activeLanguageId } = useAppState();

  const handleIsDelivery = (title: string) => {
    if (title?.toLowerCase() === "delivery") return true;

    return false;
  };

  useEffect(() => {
    if (
      !isOnViewMode &&
      (customerLocations?.customer ||
        (customerLocations?.locations && customerLocations?.locations?.length > 0))
    ) {
      setOpenInMaintenanceMode(true);
    }
  }, [customerLocations, isOnViewMode]);

  const { isFetching: loadingTimezone } = useQuery(
    ["timezones"],
    () =>
      TimezoneService.getAll({
        sortBy: JSON.stringify(["gmtOffset"]),
      }),
    {
      enabled: !externalList?.timezoneList && !onCustomerView,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) setTimezones(response.data);
      },
    }
  );

  const titleAddress = useMemo(() => {
    const titles = {
      [translate("pickup").toLowerCase()]: translate("pickup"),
      [translate("delivery").toLowerCase()]: translate("delivery"),
    };

    return titles[title.toLowerCase()];
  }, [title, translate]);

  const handleReturnCustomerOrAddress = (type: string): string => {
    if (type === "customer") {
      if (customerLocations?.customer) {
        const { name, lastName, fullName, abbreviation } = customerLocations.customer;

        return `${abbreviation || ""} ${name || ""} ${lastName || ""}` || fullName || "";
      }

      return "";
    }

    return (customerLocations?.locations && customerLocations.locations[0].longAddress) || "";
  };

  const onUpdateAddress = (locations: Location[] | DifferentLocation[]) => {
    if (update)
      update({
        customer: customerLocations?.customer,
        contacts: customerLocations?.contacts,
        locations,
      });
  };

  const renderIcon = () => {
    if (openInMaintenanceMode) {
      return <ExpandMore />;
    }

    if (isLoading || loadingTimezone) {
      return <CircularProgress size={15} />;
    }

    return <ExpandLess />;
  };

  const renderCustomerAttribute = ({
    icon,
    label,
    dataCy,
    options,
  }: RenderCustomerAttributeProps) => (
    <InfoWrapper item xs={12} lg={4} data-cy={dataCy} {...options}>
      <div className={classes.iconWrapper}>{icon}</div>
      <Tooltip title={label || ""} placement="top">
        <span className={classes.infosLetterCSS}>{label}</span>
      </Tooltip>
    </InfoWrapper>
  );

  useEffect(() => {
    if (
      activeLanguageId !== undefined &&
      externalList?.languages &&
      externalList?.languages?.length > 1
    ) {
      const indexOfActiveLanguage = externalList?.languages.findIndex(
        (language) => language.id === activeLanguageId
      );
      const activeLanguage = externalList.languages[indexOfActiveLanguage];

      if (indexOfActiveLanguage > 0) {
        const languagesAux = externalList.languages;

        languagesAux.splice(indexOfActiveLanguage, 1);
        languagesAux.unshift(activeLanguage);

        setOrderLanguages(languagesAux);
      } else {
        setOrderLanguages(externalList.languages);
      }
    } else if (externalList?.languages && externalList?.languages?.length) {
      setOrderLanguages(externalList.languages);
    }
  }, [activeLanguageId, externalList?.languages]);

  const renderCustomerSectionOnViewMode = () => {
    if (!isRenderOnViewComponent) {
      return (
        <p data-cy="customer-name" className="d-flex align-items-center">
          <span className={classes.iconWhitespace}>
            <FiUser size={20} />
          </span>
          <span className={classes.labelCustomerOrAddress}>
            {handleReturnCustomerOrAddress("customer")}
          </span>
        </p>
      );
    }

    if (customerLocations?.customer) {
      const {
        customer: { fullName, email, phone, timezoneId, destinationMobile },
      } = customerLocations;

      const getPhone = () => {
        if (handleIsDelivery(titleAddress) && destinationMobile) return destinationMobile;

        return phone;
      };

      const timezone = timezones?.find((time) => Number(time.id) === Number(timezoneId));
      const customerValues: RenderCustomerAttributeProps[] = [
        { icon: <FiUser size={20} />, label: fullName ?? "", dataCy: "customer-name" },
        {
          icon: <MdOutlineEmail size={20} />,
          label: email ?? "",
          dataCy: "customer-email",
        },
        {
          icon: <HiOutlinePhone size={20} />,
          label: getPhone() ?? "",
          dataCy: "customer-phone",
        },
      ];

      return (
        <>
          <ViewModeTitle>{translate("customer")}</ViewModeTitle>
          <SectionWrapper container direction="column" spacing={4}>
            <Grid container spacing={2} direction="row" item xs={12}>
              <ContactInfoWrapper item xs={12} container spacing={2}>
                {customerValues?.map((values) => {
                  return renderCustomerAttribute(values);
                })}
              </ContactInfoWrapper>
            </Grid>
          </SectionWrapper>
          {timezone && (
            <Grid
              item
              container
              xs={12}
              spacing={2}
              className={classnames(classes.mb16, classes.mt3)}
            >
              {renderCustomerAttribute({
                icon: <VscGlobe size={20} />,
                label: timezone
                  ? `${timezone?.nameFormatted ?? ""} (GMT${timezone?.gmtOffset ?? ""})`
                  : "",
                dataCy: "customer-timezone",
                options: { md: 12, lg: 12 },
              })}
            </Grid>
          )}
        </>
      );
    }
  };

  const renderSecondContactsOnViewMode = () => {
    const renderContacts = () => {
      if (customerLocations?.contacts)
        return (
          <Grid container spacing={2} direction="row" item xs={12}>
            {customerLocations?.contacts.map((contact) => {
              return (
                <ContactInfoWrapper item xs={12} container spacing={2} key={contact.customerId}>
                  <InfoWrapper item xs={12} lg={4}>
                    <div className={classes.iconWrapper}>
                      <FiUser size={20} />
                    </div>
                    <Tooltip
                      title={`${contact.firstName ?? ""} ${contact.lastName ?? ""}` || ""}
                      placement="top"
                    >
                      <span>{`${contact.firstName ?? ""} ${contact.lastName ?? ""}`}</span>
                    </Tooltip>
                  </InfoWrapper>
                  <InfoWrapper item xs={12} lg={4}>
                    <div className={classes.iconWrapper}>
                      <MdOutlineEmail size={20} />
                    </div>
                    <Tooltip title={contact.email ?? ""} placement="top">
                      <span>{contact.email ?? ""}</span>
                    </Tooltip>
                  </InfoWrapper>
                  <InfoWrapper item xs={12} lg={4}>
                    <div className={classes.iconWrapper}>
                      <HiOutlinePhone size={20} />
                    </div>
                    <Tooltip title={formatPhoneNumber(contact.mobilePhone) ?? ""} placement="top">
                      <span>{formatPhoneNumber(contact.mobilePhone) ?? ""}</span>
                    </Tooltip>
                  </InfoWrapper>
                </ContactInfoWrapper>
              );
            })}
          </Grid>
        );
    };

    if (customerLocations?.contacts && customerLocations?.contacts.length >= 1) {
      return (
        <>
          <ViewModeDivider />
          <ViewModeTitle>{translate("secondContact")}</ViewModeTitle>
          <SectionWrapper container direction="column" spacing={4}>
            {renderContacts()}
          </SectionWrapper>
        </>
      );
    }
  };

  const renderAddressOnViewMode = () => {
    const renderAddress = () => {
      if (customerLocations?.locations)
        return (
          <Grid container spacing={2} direction="row" item xs={12}>
            {customerLocations.locations.map((location) => {
              return (
                <ContactMapInfoWrapper item xs={12} key={location.id}>
                  <InfoWrapper>
                    <div className={classes.iconWrapper}>
                      <FiMapPin size={20} />
                    </div>
                    <span>{location.longAddress ?? location.country ?? ""}</span>
                  </InfoWrapper>
                </ContactMapInfoWrapper>
              );
            })}
          </Grid>
        );
    };

    if (customerLocations?.locations && customerLocations?.locations.length >= 1) {
      return (
        <>
          <ViewModeDivider />
          <ViewModeTitle>{translate("address")}</ViewModeTitle>
          <SectionWrapper container direction="column" spacing={4}>
            {renderAddress()}
          </SectionWrapper>
        </>
      );
    }
  };

  useEffect(() => {
    if (!timezones?.length && externalList?.timezoneList?.length)
      setTimezones(externalList?.timezoneList);
  }, [externalList?.timezoneList, timezones]);

  const findLanguageById = useCallback(
    (customer?: Customer) => {
      if (
        customer?.languageId === null &&
        externalList?.languages &&
        externalList?.languages.length > 0
      ) {
        const langValue = externalList?.languages.find(
          (languages) => languages.id === activeLanguageId
        );

        setCustomerLanguage(langValue);
      } else if (externalList?.languages && externalList?.languages.length > 0 && customer) {
        const langValue = externalList?.languages.find(
          (languages) => languages.id === customer?.languageId
        );

        setCustomerLanguage(langValue);
      }
    },
    [customerLocations.customer, externalList?.languages]
  );

  useEffect(
    () => findLanguageById(customerLocations.customer),
    [customerLocations.customer, externalList?.languages]
  );

  return (
    <div className={classes.container} data-cy={title}>
      <RemoveItemsFromLocationModal
        ref={removeItemsRef}
        onAccept={(locationId: string | number) => {
          onConfirmRemoveAddress({
            addressId: locationId,
            customerDestinationLocation: customerLocations,
            inventory,
            updateAddress: (inventory, customerLocation) =>
              updateAll && updateAll(inventory, customerLocation),
          });
        }}
        onDecline={() => {}}
      />
      <Box customClasses={classes.mainDiv}>
        <div>
          <div
            className={onCustomerView && !isMobile ? "" : classes.titleContainer}
            onClick={() =>
              onCustomerView && !isMobile
                ? null
                : isOnViewMode
                ? setOpenInViewMode((prevState) => !prevState)
                : setOpenInMaintenanceMode((prevState) => !prevState)
            }
          >
            <label
              className={`${classes.labelAddress} ${
                isOnViewMode ? classes.titleAddressOnView : ""
              }`}
            >
              {titleAddress} {(!isOnViewMode || !onCustomerView) && <Required>*</Required>}
            </label>
            {((isOnViewMode && !onCustomerView) || (onCustomerView && isMobile)) && (
              <IconButton className={classes.arrowAngle} data-cy="open-button" component="span">
                {openInViewMode ? <ExpandMore /> : <ExpandLess />}
              </IconButton>
            )}
            {openInViewMode && !isOnViewMode && (
              <IconButton
                disabled={isLoading}
                className={classes.arrowAngle}
                data-cy="open-button"
                component="span"
              >
                {renderIcon()}
              </IconButton>
            )}
          </div>
          {openInViewMode && isRenderOnViewComponent && (
            <>
              <Grid
                container
                direction="row"
                alignItems="center"
                justifyContent={!isOnViewMode ? "space-between" : undefined}
                spacing={!isOnViewMode ? 2 : undefined}
                className={isOnViewMode ? "mt-3" : ""}
              >
                <Grid item container direction="column" xs={isOnViewMode ? 12 : 11}>
                  {!onCustomerView && renderCustomerSectionOnViewMode()}
                  {!onCustomerView && renderSecondContactsOnViewMode()}
                  {!onCustomerView && renderAddressOnViewMode()}
                  {isOnViewMode &&
                    showMilitaryLocationExtensionInView &&
                    locationExtension &&
                    hasNonEmptyLocationExtension(locationExtension) && (
                      <>
                        <ViewModeDivider />
                        <Grid container spacing={2} direction="row" alignItems="flex-start">
                          <Grid item xs={12} sm={6} md={3}>
                            <FieldView
                              label={translate("baseCode")}
                              value={locationExtension.baseCode ?? ""}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FieldView
                              label={translate("baseDescription")}
                              value={locationExtension.baseDescription ?? ""}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FieldView
                              label={translate("areaCode")}
                              value={locationExtension.areaCode ?? ""}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <FieldView
                              label={translate("areaDescription")}
                              value={locationExtension.areaDescription ?? ""}
                            />
                          </Grid>
                        </Grid>
                        <ViewModeTitle className={classes.mb16} style={{ paddingTop: 16 }}>{translate("agent")}</ViewModeTitle>
                        <Grid container spacing={2} direction="row" alignItems="flex-start">
                          <Grid item xs={12} sm={6} md={4}>
                            <FieldView
                              label={translate("agentName")}
                              value={locationExtension.agentName ?? ""}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FieldView
                              label={translate("zipCode")}
                              value={locationExtension.agentZip ?? ""}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <FieldView
                              label={translate("city")}
                              value={locationExtension.agentCity ?? ""}
                            />
                          </Grid>
                        </Grid>
                      </>
                    )}
                  {onCustomerView &&
                    customerLocations.locations?.map(
                      (location) =>
                        !location.id?.toString().includes("delete") &&
                        location.longAddress && (
                          <p
                            data-cy="customer-address"
                            key={location.id}
                            className="d-flex align-items-center"
                          >
                            <span className={classes.iconWhitespace}>
                              <FiMapPin />
                            </span>
                            <span className={classes.labelCustomerOrAddress}>
                              {location.longAddress}
                            </span>
                          </p>
                        )
                    )}
                </Grid>
                {!isOnViewMode && (
                  <Grid item container xs={1} justifyContent="flex-end">
                    <IconButton
                      disabled={isLoading}
                      className={classes.arrowAngle}
                      data-cy="open-button"
                      component="span"
                      onClick={() => setOpenInMaintenanceMode(!openInMaintenanceMode)}
                    >
                      {renderIcon()}
                    </IconButton>
                  </Grid>
                )}
              </Grid>
              {onCustomerView && access && (
                <AccessChecklistSummary access={access} onCustomerView />
              )}
            </>
          )}
        </div>

        {(openInMaintenanceMode || !!externalErrors?.length) && (
          <div className={classes.addressForm} data-cy="address-form">
            <Grid item container direction="row" xs={11} className={classes.infoIcons}>
              <p data-cy="customer-name" className={classes.infoIcons}>
                <span className={classes.iconWhitespace}>
                  <FiUser />
                </span>
                <span className={classes.labelCustomerOrAddress}>
                  {handleReturnCustomerOrAddress("customer")}
                </span>
              </p>
              {customerLocations.locations?.map(
                (location) =>
                  !location.id?.toString().includes("delete") && (
                    <p data-cy="customer-address" key={location.id} className={classes.infoIcons}>
                      <span className={classes.iconWhitespace}>
                        <FiMapPin />
                      </span>
                      <span className={classes.labelCustomerOrAddress}>{location.longAddress}</span>
                    </p>
                  )
              )}
            </Grid>
            <CustomerInfoForm
              isDelivery={handleIsDelivery(titleAddress)}
              customer={customerLocations.customer}
              contacts={customerLocations.contacts}
              typeAddress={title}
              update={(value: CustomerLocations) => {
                if (update) {
                  update({
                    ...value,
                    locations: customerLocations.locations,
                  });
                }
              }}
              onCopyInfo={() => onCopyInfo && onCopyInfo()}
              timezones={timezones}
              externalErrors={externalErrors}
              contactsErrors={contactsErrors}
              languageOptions={orderLanguages}
              customerLanguage={customerLanguage}
              userLanguageId={activeLanguageId}
            />
            <hr className={classes.hr} />
            <AddressInfoForm
              isExpandedAddress={true}
              condition={title}
              addressesArray={customerLocations.locations || []}
              update={onUpdateAddress}
              onRemoveAddress={(from: string, locationIndex: number) =>
                onRemoveAddress({
                  from,
                  locationIndex,
                  customerLocation: customerLocations,
                  modalReference: removeItemsRef,
                  inventory,
                  updateAddress: (locations: CustomerLocations) => update && update(locations),
                })
              }
              hasError={fieldHasError("address")}
              errorMessage={getErrorByField("address")}
              disableMapInput={
                !(customerLocations.customer?.id || customerLocations.customer?.companyId)
              }
            />
            {onLocationExtensionChange && (
              <>
                <hr className={classes.hr} />
                <Grid container spacing={2} direction="row" alignItems="flex-start">
                  <Grid item xs={12} md={3}>
                    <Input
                      name="locationExtensionBaseCode"
                      label={translate("baseCode")}
                      value={locationExtension?.baseCode ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onLocationExtensionChange("baseCode", e.currentTarget.value)
                      }
                      placeholder={translate("baseCode")}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Input
                      name="locationExtensionBaseDescription"
                      label={translate("baseDescription")}
                      value={locationExtension?.baseDescription ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onLocationExtensionChange("baseDescription", e.currentTarget.value)
                      }
                      placeholder={translate("baseDescription")}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Input
                      name="locationExtensionAreaCode"
                      label={translate("areaCode")}
                      value={locationExtension?.areaCode ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onLocationExtensionChange("areaCode", e.currentTarget.value)
                      }
                      placeholder={translate("areaCode")}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Input
                      name="locationExtensionAreaDescription"
                      label={translate("areaDescription")}
                      value={locationExtension?.areaDescription ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onLocationExtensionChange("areaDescription", e.currentTarget.value)
                      }
                      placeholder={translate("areaDescription")}
                    />
                  </Grid>
                </Grid>
                <div className={classes.mb16} />
                <ViewModeTitle className={classes.mb16} style={{ paddingTop: 16 }}>{translate("agent")}</ViewModeTitle>
                <Grid container spacing={2} direction="row" alignItems="flex-start">
                  <Grid item xs={12} md={4}>
                    <Input
                      name="locationExtensionAgentName"
                      label={translate("agentName")}
                      value={locationExtension?.agentName ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onLocationExtensionChange("agentName", e.currentTarget.value)
                      }
                      placeholder={translate("agentName")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Input
                      name="locationExtensionAgentZip"
                      label={translate("zipCode")}
                      value={locationExtension?.agentZip ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onLocationExtensionChange("agentZip", e.currentTarget.value)
                      }
                      placeholder={translate("zipCode")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Input
                      name="locationExtensionAgentCity"
                      label={translate("city")}
                      value={locationExtension?.agentCity ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onLocationExtensionChange("agentCity", e.currentTarget.value)
                      }
                      placeholder={translate("city")}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            {access && <AccessChecklistSummary access={access} />}
          </div>
        )}
      </Box>
    </div>
  );
};

export default AddressComponent;
